/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectEmbedToken, selectTabList } from 'modules/dashboard/selectors';
import {
  selectTrendHotelFilterList,
  selectIsCumulative,
  selectTrendHotelNormalizedFilterList,
  selectTrendTimeWeeklyView,
  selectTrendHotelVisuals,
  selectWeeklyTrendHotelVisuals,
  selectAvgLosIsDisplayTH,
  selectAvgBookingWindowIsDisplayTH,
  selectAdrIsDisplayTH,
  selectCxlIsDisplayTH,
  selectOccupiedRoomsIsDisplayTH,
  selectRevenueIsDisplayTH,
  selectTrendHotelCumulativeVisuals,
  selectIsPercentageChange,
  selectTrendHotelNormalizedView,
  selectTrendHotelPercentageVisuals,
  selectIsForecast,
  selectTrendHotelForecastOffVisuals,
  selectTrendHotelForecastOffCumulativeVisuals,
  selectWeeklyTrendHotelForecastOffVisuals,
  selectTrendHotelForecastOffPercentageVisuals,
  selectMovingAverageVisualTH,
  selectIsMovingAverage,
  selectEnableSecondaryHotelGroups,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import VisualGrid from 'modules/dashboard/components/tab-container/visual-grid';
import { trendActions } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import { EXPORT_OPTIONS, REPORT_TYPE } from 'modules/dashboard/constants';
import { findVisual } from 'modules/dashboard/components/tab-container/trend-tabs/functions';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import { AssistantIcon } from 'modules/common/components';
import { isEmpty } from '../../../../../../../../common/helpers/object';
/**
 * Trend Hotel graphical report with filters
 * @param {Function} setIsHotelLoaded - set whether trend hotel graphical visuals are loaded
 * @returns
 */
const TrendHotelGraphicalWidget = ({ setIsHotelLoaded }) => {
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const filterList = useSelector(selectTrendHotelFilterList);
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const isCumulative = useSelector(selectIsCumulative);
  const normalizedFilterList = useSelector(selectTrendHotelNormalizedFilterList);
  const visuals = useSelector(selectTrendHotelVisuals);
  const cumulativeVisuals = useSelector(selectTrendHotelCumulativeVisuals);
  const weeklyVisuals = useSelector(selectWeeklyTrendHotelVisuals);
  const tokenDetails = useSelector(selectEmbedToken);
  const revenueIsDisplay = useSelector(selectRevenueIsDisplayTH);
  const occupiedRoomsIsDisplay = useSelector(selectOccupiedRoomsIsDisplayTH);
  const adrIsDisplay = useSelector(selectAdrIsDisplayTH);
  const avgBookingWindowIsDisplay = useSelector(selectAvgBookingWindowIsDisplayTH);
  const avgLosIsDisplay = useSelector(selectAvgLosIsDisplayTH);
  const cxlIsDisplay = useSelector(selectCxlIsDisplayTH);
  const percentageVisuals = useSelector(selectTrendHotelPercentageVisuals);
  const isPercentageChange = useSelector(selectIsPercentageChange);
  const normalizedViewToggle = useSelector(selectTrendHotelNormalizedView);
  const forecast = useSelector(selectIsForecast);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const forecastOffVisuals = useSelector(selectTrendHotelForecastOffVisuals);
  const forecastOffCumulativeVisuals = useSelector(selectTrendHotelForecastOffCumulativeVisuals);
  const forecastOffWeeklyVisuals = useSelector(selectWeeklyTrendHotelForecastOffVisuals);
  const forecastOffPercentageVisuals = useSelector(selectTrendHotelForecastOffPercentageVisuals);
  const movingAverageVisuals = useSelector(selectMovingAverageVisualTH);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  //
  const [isInitialTotalRevenueLoaded, setIsInitialTotalRevenueLoaded] = useState(false);
  const [isInitialTotalOccupiedRoomsLoaded, setIsInitialTotalOccupiedRoomsLoaded] = useState(false);
  const [isInitialAdrLoaded, setIsInitialAdrLoaded] = useState(false);
  const [isInitialAvgBookingWindowLoaded, setIsInitialAvgBookingWindowLoaded] = useState(false);
  const [isInitialCancellationsLoaded, setIsInitialCancellationsLoaded] = useState(false);
  const [isInitialAvgLoSLoaded, setIsInitialAvgLoSLoaded] = useState(false);
  const [isTrendForecast, setIsTrendForecast] = useState(false);
  //
  const [revenueReport, setRevenueReport] = useState({});
  const [occupiedReport, setOccupiedReport] = useState({});
  const [adrReport, setAdrReport] = useState({});
  const [bookingWindowReport, setBookingWindowReport] = useState({});
  const [stayDailyReport, setStayDailyReport] = useState({});
  const [countDailyReport, setCountDailyReport] = useState({});
  const [isRevenueRendered, setIsRevenueRendered] = useState(false);
  const [isOccupiedRendered, setIsOccupiedRendered] = useState(false);
  const [isAdrRendered, setIsAdrRendered] = useState(false);
  const [isBookingWindowRendered, setIsBookingWindowRendered] = useState(false);
  const [isStayDailyRendered, setIsStayDailyRendered] = useState(false);
  const [isCountDailyRendered, setIsCountDailyRendered] = useState(false);
  const [isV1Processing, setIsV1Processing] = useState(false);
  const [isV2Processing, setIsV2Processing] = useState(false);
  const [isV3Processing, setIsV3Processing] = useState(false);
  const [isV4Processing, setIsV4Processing] = useState(false);
  const [isV5Processing, setIsV5Processing] = useState(false);
  const [isV6Processing, setIsV6Processing] = useState(false);
  // To get visual details
  const getVisual = (index) => {
    let found;
    if (isCumulative) {
      found = findVisual(
        isTrendForecast ? cumulativeVisuals : forecastOffCumulativeVisuals,
        index,
        weeklyViewToggle,
        isTrendForecast
          ? [REPORT_TYPE.WEEKLY_TREND_HOTEL, REPORT_TYPE.TREND_HOTEL]
          : [REPORT_TYPE.WEEKLY_TREND_HOTEL_FORECAST_OFF, REPORT_TYPE.TREND_HOTEL_FORECAST_OFF]
      );
    } else if (weeklyViewToggle) {
      found = findVisual(
        isTrendForecast ? weeklyVisuals : forecastOffWeeklyVisuals,
        index,
        true,
        isTrendForecast
          ? [REPORT_TYPE.TREND_HOTEL_CUMULATIVE]
          : [REPORT_TYPE.TREND_HOTEL_CUMULATIVE_FORECAST_OFF]
      );
    } else if (isPercentageChange && !weeklyViewToggle && !isCumulative && !normalizedViewToggle) {
      found = findVisual(
        isTrendForecast ? percentageVisuals : forecastOffPercentageVisuals,
        index,
        true,
        isTrendForecast
          ? [REPORT_TYPE.TREND_HOTEL_PERCENTAGE]
          : [REPORT_TYPE.TREND_HOTEL_PERCENTAGE_FORECAST_OFF],
        true
      );
    } else if (isMovingAverage) {
      found = findVisual(movingAverageVisuals, index, true, [
        REPORT_TYPE.TREND_HOTEL_CUMULATIVE,
        REPORT_TYPE.TREND_HOTEL_MOVING_AVERAGE,
      ]);
    } else {
      found = findVisual(
        isTrendForecast ? visuals : forecastOffVisuals,
        index,
        true,
        isTrendForecast
          ? [REPORT_TYPE.TREND_HOTEL_CUMULATIVE, REPORT_TYPE.TREND_HOTEL]
          : [REPORT_TYPE.TREND_HOTEL_CUMULATIVE_FORECAST_OFF, REPORT_TYPE.TREND_HOTEL_FORECAST_OFF]
      );
    }
    return found;
  };
  useEffect(() => {
    // Calculate is Loaded  based on the given conditions
    const pair1 = revenueIsDisplay ? revenueIsDisplay && isInitialTotalRevenueLoaded : true;
    const pair2 = occupiedRoomsIsDisplay
      ? occupiedRoomsIsDisplay && isInitialTotalOccupiedRoomsLoaded
      : true;
    const pair3 = adrIsDisplay ? adrIsDisplay && isInitialAdrLoaded : true;
    const pair4 = avgBookingWindowIsDisplay
      ? avgBookingWindowIsDisplay && isInitialAvgBookingWindowLoaded
      : true;
    const pair5 = avgLosIsDisplay ? avgLosIsDisplay && isInitialAvgLoSLoaded : true;
    const pair6 = cxlIsDisplay ? cxlIsDisplay && isInitialCancellationsLoaded : true;
    if (
      revenueIsDisplay &&
      isInitialTotalRevenueLoaded &&
      occupiedRoomsIsDisplay &&
      isInitialTotalOccupiedRoomsLoaded &&
      adrIsDisplay &&
      isInitialAdrLoaded &&
      avgBookingWindowIsDisplay &&
      isInitialAvgBookingWindowLoaded &&
      avgLosIsDisplay &&
      isInitialAvgLoSLoaded &&
      cxlIsDisplay &&
      isInitialCancellationsLoaded
    ) {
      setIsHotelLoaded(true);
    } else {
      setIsHotelLoaded(pair1 || pair2 || pair3 || pair4 || pair5 || pair6);
    }
  }, [
    revenueIsDisplay,
    isInitialTotalRevenueLoaded,
    occupiedRoomsIsDisplay,
    isInitialTotalOccupiedRoomsLoaded,
    adrIsDisplay,
    isInitialAdrLoaded,
    avgBookingWindowIsDisplay,
    isInitialAvgBookingWindowLoaded,
    avgLosIsDisplay,
    isInitialAvgLoSLoaded,
    cxlIsDisplay,
    isInitialCancellationsLoaded,
  ]);
  // To set visual loading state based on forecast toggle
  useEffect(() => {
    setIsTrendForecast(forecast);
  }, [forecast]);

  // To get visuals based on the configuration
  const getWidget = (
    index,
    cssClassName,
    setPBIReport = () => {},
    setIsLoaded = () => {},
    setIsRendered = () => {},
    filter = filterList,
    displayFunction = () => {}
  ) => {
    const found = getVisual(index);
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={tokenDetails?.accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filter}
          setPBIReport={(r) => setPBIReport(r)}
          setIsLoaded={(d) => setIsLoaded(d)}
          setIsRendered={(r) => setIsRendered(r)}
          tab={TAB_VALUE.TREND_TIME}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  // To get visual names based on visual order
  const getVisualName = (order) => getVisual(order)?.name;
  //
  return (
    !isEmpty(filterList) && (
      <>
        <VisualGrid
          visualName={getVisualName(0)}
          isDisplay={revenueIsDisplay}
          visual={getWidget(
            0,
            styles.paceVisual,
            setRevenueReport,
            setIsInitialTotalRevenueLoaded,
            setIsRevenueRendered,
            normalizedFilterList,
            trendActions.setRevenueIsDisplayTH
          )}
          onClick={() => {
            dispatch(trendActions.setRevenueIsDisplayTH(!revenueIsDisplay));
          }}
          interpreterFragment={
            <AssistantIcon
              visualName={getVisualName(0)}
              report={revenueReport}
              isProcessing={isV1Processing}
              setIsProcessing={setIsV1Processing}
              disabled={!isRevenueRendered || isV1Processing || !revenueIsDisplay}
              enableSecondaryHotelGroups={enableSecondaryHotelGroups}
              exportType={exportType}
              setExportType={setExportType}
            />
          }
        />
        <VisualGrid
          visualName={getVisualName(1)}
          isDisplay={occupiedRoomsIsDisplay}
          visual={getWidget(
            1,
            styles.paceVisual,
            setOccupiedReport,
            setIsInitialTotalOccupiedRoomsLoaded,
            setIsOccupiedRendered,
            normalizedFilterList,
            trendActions.setOccupiedRoomIsDisplayTH
          )}
          onClick={() => {
            dispatch(trendActions.setOccupiedRoomIsDisplayTH(!occupiedRoomsIsDisplay));
          }}
          interpreterFragment={
            <AssistantIcon
              visualName={getVisualName(1)}
              report={occupiedReport}
              isProcessing={isV2Processing}
              setIsProcessing={setIsV2Processing}
              disabled={!isOccupiedRendered || isV2Processing || !occupiedRoomsIsDisplay}
              enableSecondaryHotelGroups={enableSecondaryHotelGroups}
              exportType={exportType}
              setExportType={setExportType}
            />
          }
        />
        <VisualGrid
          visualName={getVisualName(2)}
          isDisplay={adrIsDisplay}
          visual={getWidget(
            2,
            styles.paceVisual,
            setAdrReport,
            setIsInitialAdrLoaded,
            setIsAdrRendered,
            filterList,
            trendActions.setAdrIsDisplayTH
          )}
          onClick={() => {
            dispatch(trendActions.setAdrIsDisplayTH(!adrIsDisplay));
          }}
          interpreterFragment={
            <AssistantIcon
              visualName={getVisualName(2)}
              report={adrReport}
              isProcessing={isV3Processing}
              setIsProcessing={setIsV3Processing}
              disabled={!isAdrRendered || isV3Processing || !adrIsDisplay}
              enableSecondaryHotelGroups={enableSecondaryHotelGroups}
              exportType={exportType}
              setExportType={setExportType}
            />
          }
        />
        {getWidget(3, styles.paceVisual, () => {}, setIsInitialAvgLoSLoaded) && (
          <VisualGrid
            visualName={getVisualName(3)}
            isDisplay={avgLosIsDisplay}
            visual={getWidget(
              3,
              styles.paceVisual,
              setStayDailyReport,
              setIsInitialAvgLoSLoaded,
              setIsStayDailyRendered,
              filterList,
              trendActions.setAvgLosIsDisplayTH
            )}
            onClick={() => {
              dispatch(trendActions.setAvgLosIsDisplayTH(!avgLosIsDisplay));
            }}
            interpreterFragment={
              <AssistantIcon
                visualName={getVisualName(3)}
                report={stayDailyReport}
                isProcessing={isV4Processing}
                setIsProcessing={setIsV4Processing}
                disabled={!isStayDailyRendered || isV4Processing || !avgLosIsDisplay}
                enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                exportType={exportType}
                setExportType={setExportType}
              />
            }
          />
        )}
        {getWidget(4, styles.paceVisual, () => {}, setIsInitialAvgBookingWindowLoaded) && (
          <VisualGrid
            visualName={getVisualName(4)}
            isDisplay={avgBookingWindowIsDisplay}
            visual={getWidget(
              4,
              styles.paceVisual,
              setBookingWindowReport,
              setIsInitialAvgBookingWindowLoaded,
              setIsBookingWindowRendered,
              filterList,
              trendActions.setAvgBookingWindowIsDisplayTH
            )}
            onClick={() => {
              dispatch(trendActions.setAvgBookingWindowIsDisplayTH(!avgBookingWindowIsDisplay));
            }}
            interpreterFragment={
              <AssistantIcon
                visualName={getVisualName(4)}
                report={bookingWindowReport}
                isProcessing={isV5Processing}
                setIsProcessing={setIsV5Processing}
                disabled={!isBookingWindowRendered || isV5Processing || !avgBookingWindowIsDisplay}
                enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                exportType={exportType}
                setExportType={setExportType}
              />
            }
          />
        )}
        {getWidget(
          5,
          styles.paceVisual,
          () => {},
          setIsInitialCancellationsLoaded,
          normalizedFilterList
        ) && (
          <VisualGrid
            visualName={getVisualName(5)}
            isDisplay={cxlIsDisplay}
            visual={getWidget(
              5,
              styles.paceVisual,
              setCountDailyReport,
              setIsInitialCancellationsLoaded,
              setIsCountDailyRendered,
              normalizedFilterList,
              trendActions.setCxlIsDisplayTH
            )}
            onClick={() => {
              dispatch(trendActions.setCxlIsDisplayTH(!cxlIsDisplay));
            }}
            interpreterFragment={
              <AssistantIcon
                visualName={getVisualName(5)}
                report={countDailyReport}
                isProcessing={isV6Processing}
                setIsProcessing={setIsV6Processing}
                disabled={!isCountDailyRendered || isV6Processing || !cxlIsDisplay}
                enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                exportType={exportType}
                setExportType={setExportType}
              />
            }
          />
        )}
      </>
    )
  );
};
//
export default TrendHotelGraphicalWidget;
